import { useFormField as originalUseFormField } from '@nuxt/ui/composables/useFormField';
import { reactiveComputed } from '@vueuse/core';
import { inject, provide } from 'vue';
import { formFieldInjectionKey } from '#ui/composables/useFormField';

type Props<T> = Parameters<typeof originalUseFormField<T>>[0];
type Opts = Parameters<typeof originalUseFormField>[1];

/**
 * Wrapper around NuxtUI `useFormField` that allows forwarding the form field context.
 *
 * Normally NuxtUI clears the form field injection, to prevent duplicate emits in nested form
 * field scenarios. When overriding NuxtUI components with custom wrappers that need access to
 * the form field context, without clearing it, you can pass `forwardContext: true` to re-inject
 * the context and behave transparently.
 *
 * Pass a getter (`() => ({ name, size })`) when the component destructures its props.
 */
export function useFormField<T>(props?: Props<T> | (() => NonNullable<Props<T>>), opts?: Opts & { forwardContext?: boolean }) {
	// eslint-disable-next-line unicorn/no-useless-undefined -- Vue requires a second argument to mark injection as optional
	const formField = inject(formFieldInjectionKey, undefined);
	const result = originalUseFormField(typeof props === 'function' ? reactiveComputed(props) : props, opts);

	if (opts?.forwardContext) {
		provide(formFieldInjectionKey, formField);
	}

	return result;
}
